import React, { useState } from "react";
import {
  Modal,
  Button,
  Stack,
  Text,
  Group,
  Paper,
  ActionIcon,
  Select,
  TextInput,
  Alert,
  Badge,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconPlus,
  IconTrash,
  IconUsers,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { JourneyStudent } from "./journey.types";

interface Props {
  opened: boolean;
  onClose: () => void;
  currentStudents: JourneyStudent[];
  studentsDropdown: { label: string; value: string; name: string }[];
  onSave: (students: JourneyStudent[]) => Promise<void>;
  isSubmitting: boolean;
  onCreateStudent: () => void;
}

const ManageStudentsModal = ({
  opened,
  onClose,
  currentStudents,
  studentsDropdown,
  onSave,
  isSubmitting,
  onCreateStudent,
}: Props) => {
  const [students, setStudents] = useState<JourneyStudent[]>(currentStudents);

  // Reset students when modal opens
  React.useEffect(() => {
    if (opened) {
      setStudents([...currentStudents]);
    }
  }, [opened, currentStudents]);

  const addStudent = () => {
    setStudents((prev) => [
      ...prev,
      {
        name: "",
        email: "",
        studentClasses: "",
        attendance: "Present",
        studentStatus: "Active",
      },
    ]);
  };

  const removeStudent = (index: number) => {
    setStudents((prev) => prev.filter((_, i) => i !== index));
  };

  const updateStudent = (
    index: number,
    field: keyof JourneyStudent,
    value: any,
  ) => {
    setStudents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleStudentEmailChange = (index: number, email: string | null) => {
    if (!email) return;
    const student = studentsDropdown.find((s) => s.value === email);
    if (!student) return;

    setStudents((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        email,
        name: student.name,
      };
      return updated;
    });
  };

  const handleSave = async () => {
    // Validate all students have email and name
    const invalidStudents = students.filter((s) => !s.email || !s.name);
    if (invalidStudents.length > 0) {
      notifications.show({
        title: "Error",
        message: "All students must have email and name",
        color: "red",
      });
      return;
    }

    await onSave(students);
  };

  const handleClose = () => {
    setStudents([...currentStudents]); // Reset to original
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconUsers size={20} className="text-[#FC7A69]" />
          <Text className="text-lg font-bold text-[#221E45]">
            Manage Students
          </Text>
        </Group>
      }
      size="xl"
      styles={{
        header: {
          borderBottom: "2px solid #FC7A69",
          paddingBottom: "1rem",
        },
      }}
    >
      <Stack gap="md">
        {/* Info Alert */}
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Synchronization"
          color="blue"
          className="border-l-4 border-[#FC7A69]"
        >
          <Text size="xs" className="text-[#221E45]/80">
            Changes to students will be synchronized with the Batch Table.
          </Text>
        </Alert>

        {/* Add Student Button */}
        <Group justify="space-between">
          <Badge size="sm" className="bg-[#FC7A69]/10 text-[#FC7A69]">
            {students.length} Student(s)
          </Badge>
          <Group gap="xs">
            {/* <Button
              size="xs"
              variant="outline"
              leftSection={<IconPlus size={14} />}
              onClick={onCreateStudent}
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              Create New Student
            </Button> */}
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={addStudent}
              className="bg-[#FC7A69] hover:bg-[#FC7A69]/90 text-white"
            >
              Add Existing Student
            </Button>
          </Group>
        </Group>

        {/* Students List */}
        <Stack gap="sm" style={{ maxHeight: "400px", overflowY: "auto" }}>
          {students.map((student, index) => (
            <Paper
              key={index}
              p="sm"
              withBorder
              radius="md"
              className="border-[#FC7A69]/20"
            >
              <Group justify="space-between" mb="xs">
                <Text fw={600} size="sm" className="text-[#221E45]">
                  Student {index + 1}
                </Text>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => removeStudent(index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>

              <Stack gap="xs">
                <Select
                  label="Student Email"
                  data={studentsDropdown}
                  value={student.email}
                  onChange={(email) => handleStudentEmailChange(index, email)}
                  searchable
                  required
                  styles={{
                    input: { fontSize: "0.75rem" },
                    label: { fontSize: "0.7rem", fontWeight: 600 },
                  }}
                />
                <TextInput
                  label="Student Name"
                  value={student.name}
                  readOnly
                  styles={{
                    input: {
                      fontSize: "0.75rem",
                      backgroundColor: "#f5f5f5",
                    },
                    label: { fontSize: "0.7rem", fontWeight: 600 },
                  }}
                />
                <Group grow>
                  <TextInput
                    label="Number of Classes"
                    value={student.studentClasses}
                    onChange={(e) =>
                      updateStudent(index, "studentClasses", e.target.value)
                    }
                    styles={{
                      input: { fontSize: "0.75rem" },
                      label: { fontSize: "0.7rem", fontWeight: 600 },
                    }}
                  />
                  <Select
                    label="Attendance"
                    data={["Present", "Not Schedule", "Leave", "Holiday"]}
                    value={student.attendance}
                    onChange={(v) =>
                      updateStudent(index, "attendance", v || "Present")
                    }
                    styles={{
                      input: { fontSize: "0.75rem" },
                      label: { fontSize: "0.7rem", fontWeight: 600 },
                    }}
                  />
                  <Select
                    label="Student Status"
                    data={["Active", "Hold", "Absconded"]}
                    value={(student as any).studentStatus || "Active"}
                    onChange={(v) =>
                      updateStudent(index, "studentStatus" as any, v || "Active")
                    }
                    styles={{
                      input: { fontSize: "0.75rem" },
                      label: { fontSize: "0.7rem", fontWeight: 600 },
                    }}
                  />
                </Group>
              </Stack>
            </Paper>
          ))}

          {students.length === 0 && (
            <Paper p="xl" className="text-center bg-gray-50" radius="md">
              <Text size="sm" className="text-gray-500">
                No students added yet. Click "Add Student" to get started.
              </Text>
            </Paper>
          )}
        </Stack>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm" mt="md">
          <Button
            variant="subtle"
            onClick={handleClose}
            className="text-[#221E45] hover:bg-[#221E45]/10"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSubmitting}
            className="bg-[#FC7A69] hover:bg-[#FC7A69]/90 text-white"
            leftSection={<IconCheck size={16} />}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ManageStudentsModal;
